/**
 * https://ui.shadcn.com/docs/components/drawer
 */
"use client";

import { overlay } from "overlay-kit";
import * as React from "react";
import { useCallback, useMemo } from "react";
import { Drawer as DrawerPrimitive } from "vaul";

import { cn } from "@/lib/utils";

const BottomSheet = ({ shouldScaleBackground = true, ...props }: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
  <DrawerPrimitive.Root shouldScaleBackground={shouldScaleBackground} {...props} />
);
BottomSheet.displayName = "BottomSheet";

const BottomSheetTrigger = DrawerPrimitive.Trigger;

const BottomSheetPortal = DrawerPrimitive.Portal;

const BottomSheetClose = DrawerPrimitive.Close;

const BottomSheetOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn("fixed inset-0 z-[52] bg-black !opacity-40", className)}
    {...props}
  />
));
BottomSheetOverlay.displayName = DrawerPrimitive.Overlay.displayName;

const BottomSheetContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <BottomSheetPortal>
    <BottomSheetOverlay />
    <DrawerPrimitive.Content
      ref={ref}
      className={cn(
        "fixed inset-x-0 bottom-0 z-[52] mt-[244px] flex flex-col rounded-t-[16px] border bg-white",
        className,
      )}
      {...props}
    >
      <div className="mx-auto mt-2.5 h-1 w-[36px] rounded-full bg-borderLight" />
      {children}
    </DrawerPrimitive.Content>
  </BottomSheetPortal>
));
BottomSheetContent.displayName = "BottomSheetContent";

const BottomSheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("grid gap-1.5 px-4 pt-4 pb-5 text-center sm:text-left", className)} {...props} />
);
BottomSheetHeader.displayName = "BottomSheetHeader";

const BottomSheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("mt-auto flex flex-col gap-2 px-4 pb-4", className)} {...props} />
);
BottomSheetFooter.displayName = "BottomSheetFooter";

const BottomSheetTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props}
  />
));
BottomSheetTitle.displayName = DrawerPrimitive.Title.displayName;

const BottomSheetDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
BottomSheetDescription.displayName = DrawerPrimitive.Description.displayName;

export function useBottomSheet() {
  let id: string | undefined;
  const open = useCallback(
    async (props: {
      title: string;
      description?: string;
      children: React.ReactNode;
      className?: string;
      withNoHeader?: boolean;
    }) => {
      id = await overlay.open(({ isOpen, close }: { isOpen: boolean; close: () => void }) => {
        return (
          <BottomSheet open={isOpen} onOpenChange={close}>
            <BottomSheetContent className={cn("px-6", props.className)}>
              {props.withNoHeader ? (
                <BottomSheetTitle className="sr-only">{props.title}</BottomSheetTitle>
              ) : (
                <BottomSheetHeader>
                  <BottomSheetTitle>{props.title}</BottomSheetTitle>
                  {props.description && <BottomSheetDescription>{props.description}</BottomSheetDescription>}
                </BottomSheetHeader>
              )}
              {props.children}
            </BottomSheetContent>
          </BottomSheet>
        );
      });
    },
    [id],
  );

  return useMemo(() => ({ open, close: () => id && overlay.close(id) }), [open, id]);
}

export {
  BottomSheet,
  BottomSheetPortal,
  BottomSheetOverlay,
  BottomSheetTrigger,
  BottomSheetClose,
  BottomSheetContent,
  BottomSheetHeader,
  BottomSheetFooter,
  BottomSheetTitle,
  BottomSheetDescription,
};
